import { Button, Tooltip } from "antd";
import type { ButtonProps } from "antd";
import { EyeOutlined } from "@ant-design/icons";

import RequestView from "../RequestView";
import { ChatCompletionRequest } from "../../../model_gen/chat";
import { Link } from "react-router-dom";

export const PrimaryButton: React.FC<
  Omit<ButtonProps, "type"> & {
    isLeadingIcon?: boolean;
    children?: React.ReactNode | string;
  }
> = ({ isLeadingIcon = true, title, children, ...props }) => {
  return (
    <Button
      {...props}
      iconPosition={isLeadingIcon ? "start" : "end"}
      type="primary">
      {children ? children : title}
    </Button>
  );
};
export const DashedButton: React.FC<
  Omit<ButtonProps, "type"> & {
    isLeadingIcon?: boolean;
    children?: React.ReactNode | string;
  }
> = ({ isLeadingIcon = true, title, children, ...props }) => {
  return (
    <Button
      {...props}
      iconPosition={isLeadingIcon ? "start" : "end"}
      type="dashed">
      {children ? children : title}
    </Button>
  );
};
export const OutlineButton: React.FC<
  Omit<ButtonProps, "type"> & {
    isLeadingIcon?: boolean;
    children?: React.ReactNode | string;
  }
> = ({ isLeadingIcon = true, title, children, ...props }) => {
  return (
    <Button
      {...props}
      iconPosition={isLeadingIcon ? "start" : "end"}
      type="default">
      {children ? children : title}
    </Button>
  );
};
export const GhostButton: React.FC<
  Omit<ButtonProps, "type"> & {
    isLeadingIcon?: boolean;
    children?: React.ReactNode | string;
  }
> = ({ isLeadingIcon = true, title, children, ...props }) => {
  return (
    <Button
      {...props}
      iconPosition={isLeadingIcon ? "start" : "end"}
      type="text">
      {children ? children : title}
    </Button>
  );
};
export const LinkButton: React.FC<
  Omit<ButtonProps, "type"> & {
    isLeadingIcon?: boolean;
    children?: React.ReactNode | string;
  }
> = ({ isLeadingIcon = true, title, children, ...props }) => {
  return (
    <Button
      {...props}
      iconPosition={isLeadingIcon ? "start" : "end"}
      type="link">
      {children ? children : title}
    </Button>
  );
};

// New BadgeButton component
export const BadgeButton: React.FC<
  ButtonProps & {
    badgeCount?: number;
    badgeColor?: string;
    badgePosition?: {
      top?: string;
      right?: string;
      bottom?: string;
      left?: string;
    };
  }
> = ({
  badgeCount = 0,
  badgeColor = "#ff4d4f",
  badgePosition = { top: "7px", right: "-3px" },
  children,
  ...props
}) => {
  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <Button {...props}>{children}</Button>
      {badgeCount > 0 && (
        <span
          style={{
            position: "absolute",
            top: badgePosition.top,
            right: badgePosition.right,
            bottom: badgePosition.bottom,
            left: badgePosition.left,
            backgroundColor: badgeColor,
            color: "#fff",
            borderRadius: "50%",
            fontSize: "10px",
            minWidth: "16px",
            height: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 0 0 1px #fff",
            zIndex: 1,
          }}>
          {badgeCount > 99 ? "99+" : badgeCount}
        </span>
      )}
    </div>
  );
};

export const RequestViewButton: React.FC<
  Omit<ButtonProps, "type"> & {
    isLeadingIcon?: boolean;
    children?: React.ReactNode | string;
    apiKey: string;
    request?: ChatCompletionRequest;
    requestId?: string;
    applicationId?: string;
    applicationActionId?: string;
    isEditable?: boolean;
    onUpdated?: (updatedRequest: ChatCompletionRequest) => void;
    to?: string;
    buttonStyle?: React.CSSProperties;
    showIcon?: boolean;
    systemPrompt?: string;
    testSetId?: string;
    testSetRequestId?: string;
  }
> = ({
  apiKey,
  request,
  requestId,
  applicationId,
  applicationActionId,
  onClick,
  isEditable = false,
  onUpdated,
  children,
  to,
  size,
  buttonStyle,
  showIcon = true,
  systemPrompt,
  testSetId,
  testSetRequestId,
}) => {
  const button = (
    <Button
      type="text"
      className="details-button"
      style={
        buttonStyle
          ? buttonStyle
          : {
              borderRadius: "6px",
              color: "#1890ff",
              display: "flex",
              alignItems: "center",
              padding: "0 10px",
              height: "32px",
              transition: "all 0.2s",
            }
      }
      onClick={onClick}
      icon={showIcon ? <EyeOutlined style={{ marginRight: "4px" }} /> : null}
      size={size ? size : null}>
      {children}
    </Button>
  );
  return (
    <Tooltip
      title={
        <RequestView
          request={request}
          apiKey={apiKey}
          isEditable={isEditable}
          requestId={requestId}
          applicationId={applicationId}
          applicationActionId={applicationActionId}
          onUpdated={onUpdated}
          systemPrompt={systemPrompt}
          testSetId={testSetId}
          testSetRequestId={testSetRequestId}
        />
      }
      placement="left"
      overlayStyle={{
        maxWidth: "600px",
        width: "600px",
        borderRadius: "12px",
        boxShadow: "0 3px 10px rgba(0, 0, 0, 0.1)",
      }}
      color="#fff">
      {to ? (
        <Link to={to} target="_blank" style={{ display: "inline-flex" }}>
          {button}
        </Link>
      ) : (
        button
      )}
    </Tooltip>
  );
};
